<template>
  <div class="movements-container">
    <MovementSidebar :group="group" @setMovement="setMovement" />
    <GmapMap
      ref="map"
      class="map"
      :zoom="3"
      :center="center"
      :options="{ styles: mapStyles, maxZoom: maxZoom }"
      @click="onMapClick"
    >
      <ResetZoomControl :showControl="showZoomControls" @resetZoom="resetZoom" />
      <PreviousZoomControl :showControl="showZoomControls && clusterZooms.length" @previousZoom="previousZoom" />
      <LegendControl />
      <gmap-info-window
        :options="infoOptions"
        :position="infoWindowPos"
        :opened="infoWinOpen"
        @closeclick="infoWinOpen = false"
      >
        <MovementPopup :movement="hoverMovement" v-if="!clusterPopup && hoverMovement" />
        <MovementClusterPopup :movement="hoverMovement" :group="group" v-if="clusterPopup && hoverMovement" />
      </gmap-info-window>
    </GmapMap>
    <MovementTextNoteModal />
  </div>
</template>
<script>
import mapStyles from '../util/map.js';
import clusterConfig from '../util/cluster.js';
import calculateInfoWindowOffset from '../util/infoWindowOffset.js';
import MovementSidebar from './MovementSidebar.vue';
import MovementPopup from './MovementPopup.vue';
import MovementClusterPopup from './MovementClusterPopup.vue';
import LegendControl from './LegendControl.vue';
import PreviousZoomControl from './PreviousZoomControl.vue';
import ResetZoomControl from './ResetZoomControl.vue';
import MovementTextNoteModal from './MovementTextNoteModal.vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import MarkerClusterer from 'marker-clusterer-plus';
import seoMetadata from 'mixins/seoMetadata';
import FilterBuilder from 'lib/filterBuilder';

const MAX_ZOOM = 14;

export default {
  name: 'Movements',
  mixins: [seoMetadata],
  components: {
    MovementClusterPopup,
    MovementPopup,
    MovementSidebar,
    LegendControl,
    PreviousZoomControl,
    ResetZoomControl,
    MovementTextNoteModal,
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.map.$mapPromise.then(() => {
        this.cluster = new MarkerClusterer(this.map, [], clusterConfig);
        google.maps.event.addListener(this.cluster, 'clusterclick', this.onClusterClick);
        this.cluster.addMarkers(this.markers, true);

        if (this.appliedFilters.length) {
          this.cluster.fitMapToMarkers();
        }

        google.maps.event.addListener(this.map, 'zoom_changed', () => {
          this.hideSidebar();

          this.currentZoom = this.mapZoom();

          if (this.gridSizeToMinimize()) {
            this.cluster.setGridSize(1);
          } else {
            this.cluster.setGridSize(clusterConfig.gridSize);
          }
        });
      });
    });
  },
  computed: {
    ...mapGetters('artworks', {
      movements: 'artworks',
    }),
    ...mapGetters('aggregations', ['aggregation', 'aggregations']),
    ...mapGetters('artworks', ['getById']),
    ...mapGetters('params', ['appliedFilters', 'locationFilters']),
    ...mapState('route', ['query']),
    map() {
      return this.$refs.map.$mapObject;
    },
    markers() {
      const id = this?.$route?.query?.id;
      return Object.values(this.movements).map((m) => {
        let latLng = new google.maps.LatLng(m.latitude, m.longitude);
        let marker = new google.maps.Marker({
          position: latLng,
          icon: this.icon(m, m.color),
        });

        marker.set('id', m._id);
        marker.set('color', m.color);

        google.maps.event.addListener(marker, 'click', () => {
          this.group = [];
          this.setMarker(marker);
          this.setMovement(m);

          const query = this.$route.query;
          const filters = FilterBuilder.toURLParam(this.locationFilters);

          this.$router.push({
            name: 'Movements',
            query: { ...query, id: m._id },
            ...(this.locationFilters.length && { params: { filters } }),
          });
        });
        google.maps.event.addDomListener(marker, 'click', (e) => {
          const { clientX } = e.wa || {};
          if (clientX < 340) {
            this.centerMap(marker);
          }
        });
        google.maps.event.addListener(marker, 'mouseover', (event) => {
          this.showPopup(marker);
        });
        google.maps.event.addListener(marker, 'mouseout', (event) => {
          this.hidePopup();
        });

        if (id && m._id === id) {
          this.$nextTick(() => {
            this.map.setZoom(MAX_ZOOM);
            this.setMarker(marker);
            this.setMovement(m);
            this.centerMap(marker);
          });
        }

        return marker;
      });
    },
    showZoomControls() {
      return this.currentZoom !== this.initialZoom;
    },
    hoverMovement() {
      return this.currentMidx ? this.movements[this.currentMidx] : null;
    },
  },
  methods: {
    ...mapMutations('artworks', ['assign']),
    isMaxZoom() {
      return this.cluster.getMaxZoom() === this.mapZoom();
    },
    mapZoom() {
      return this.map.getZoom();
    },
    previousZoom() {
      const previousZoom = this.clusterZooms.pop();
      this.map.setZoom(previousZoom);
    },
    resetZoom() {
      this.map.setZoom(this.initialZoom);
      this.clusterZooms = [];
    },
    gridSizeToMinimize() {
      return this.cluster && this.mapZoom() === (this.cluster.getMaxZoom() || this.cluster.getMaxZoom() - 1);
    },
    centerMap(marker) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(marker.position);
      this.map.setCenter(bounds.getCenter());
    },
    onMapClick() {
      this.hideSidebar();
      this.hidePopup();

      if (this.query?.id) {
        const filters = FilterBuilder.toURLParam(this.locationFilters);
        this.$router
          .replace({ name: 'Movements', ...(this.locationFilters.length && { params: { filters } }) })
          .catch(() => {});
      }
    },
    showPopup(marker, clusterPopup = false) {
      this.infoWindowPos = marker.getPosition();

      this.infoWinOpen = true;
      this.currentMidx = marker.id;
      this.clusterPopup = clusterPopup;

      this.infoOptions.pixelOffset = calculateInfoWindowOffset(this.map, marker);
    },
    hidePopup() {
      this.infoWinOpen = false;
      this.currentMidx = null;
      this.clusterPopup = false;
    },
    hideSidebar() {
      this.marker = null;
      this.assign({ name: 'artwork', data: {} });
      this.group = [];
    },
    icon(marker, color) {
      if (marker && this.marker && this.marker.id === marker.id) {
        return {
          path: 'M31,18.7c-8,0-14.5,6.3-14.5,14.1c0,2.6,0.8,5.2,2.2,7.4L31,57.7l12.3-17.5c4.2-6.6,2.2-15.3-4.6-19.4C36.3,19.4,33.7,18.7,31,18.7z',
          strokeColor: color,
          fillColor: color,
          fillOpacity: 1,
          scale: 0.8,
          anchor: new google.maps.Point(31, 57),
        };
      } else {
        return {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          strokeWeight: 5,
          strokeColor: color,
        };
      }
    },
    setMarker(marker) {
      this.marker = marker;
    },
    setMovement(movement) {
      this.assign({ name: 'artwork', data: movement });
      this.$store.dispatch('artworks/fetchTextNotes', { id: movement._id });
    },
    onClusterClick(cluster) {
      let markers = cluster.getMarkers();
      this.cluster.zoomOnClick_ = true;

      let positions = markers.map((marker) => {
        return marker.getPosition().lat() + '_' + marker.getPosition().lng();
      });

      function unique(list) {
        return list.filter((x, i, a) => {
          return a.indexOf(x) === i;
        });
      }

      if (this.isMaxZoom() && unique(positions).length > 1) {
        this.cluster.zoomOnClick_ = false;
        let previousZoom = this.map.getZoom() - 1;
        this.map.setZoom(previousZoom);
      } else if (unique(positions).length === 1 && positions.length > 1) {
        this.cluster.zoomOnClick_ = false;
        this.centerMap(markers[0]);
        this.group = markers.map((m) => this.movements[m.id]);
        this.showPopup(markers[0], true);
        this.setMovement(this.movements[markers[0].id]);
      } else {
        this.group = [];
        this.clusterZooms.push(this.mapZoom());
      }
    },
  },
  watch: {
    aggregation(newValue, oldValye) {
      if (newValue) {
        this.hideSidebar();
      }
    },
    marker(newValue, oldValue) {
      if (oldValue) {
        oldValue.setIcon(this.icon(null, oldValue.color));
      }

      if (newValue) {
        newValue.setIcon(this.icon(newValue, newValue.color));
      }
    },
    $route(to, from) {
      const { id } = to.query;
      if (id !== from.query.id) {
        const movement = this.getById(id);
        if (movement) this.assign({ name: 'artwork', data: movement });
      }
    },
    movements(newValue, oldValue) {
      this.hideSidebar();

      if (this.cluster) {
        this.cluster.clearMarkers();
        this.cluster.addMarkers(this.markers);
      }

      if (!this.query?.id) {
        this.cluster.fitMapToMarkers();
      }
      this.initialZoom = this.mapZoom();
    },
  },
  data() {
    return {
      maxZoom: MAX_ZOOM,
      cluster: null,
      clusterZooms: [],
      initialZoom: 0,
      currentZoom: 0,
      center: {
        lat: 48.8538302,
        lng: 2.2982161,
      },
      infoWindowPos: null,
      infoWinOpen: false,
      clusterPopup: false,
      currentMidx: null,
      infoOptions: {
        disableAutoPan: true,
        pixelOffset: {},
      },
      mapStyles: mapStyles,
      marker: null,
      movement: null,
      group: [],
    };
  },
  metaInfo() {
    return {
      ...this.metaConfig({
        title: this.metaMovementsTitle,
        titleTemplate: this.metaTitleTemplate,
      }),
    };
  },
};
</script>
